// ============================
// src/lib/onboarding.ts
// ============================
export type OnboardingStepId =
  | "signup"
  | "checkEmail"
  | "basicInfo"
  | "activityLevel"
  | "primarySport"
  | "secondarySport"
  | "healthPrefs"
  | "deviceIntegrations"
  | "createGoal"
  | "payment"
  | "whatsNext";

type Step = { id: OnboardingStepId; path: string; label: string };

export const steps: Step[] = [
  { id: "signup", path: "/signup", label: "Sign up" },
  { id: "checkEmail", path: "/check-email", label: "Check Email" },
  { id: "basicInfo", path: "/basic-info", label: "Basic Info" },
  { id: "activityLevel", path: "/activity-level", label: "Activity Level" },
  { id: "primarySport", path: "/primary-sport", label: "Primary Sport" },
  { id: "secondarySport", path: "/secondary-sport", label: "Secondary Sport" },
  { id: "healthPrefs", path: "/health-prefs", label: "Health & Preferences" },
  { id: "deviceIntegrations", path: "/device-integrations", label: "Device Integrations" },
  { id: "createGoal", path: "/create-goal", label: "Create a goal" },
  { id: "payment", path: "/payment", label: "Payment" },
  { id: "whatsNext", path: "/whats-next", label: "What’s next" },
];

const STORAGE_KEY = "onboarding.completed.step"; // stores last completed stepId

export function getStepIndexByPath(path: string) {
  return steps.findIndex(s => s.path === path);
}

export function getStepIndexById(id: OnboardingStepId) {
  return steps.findIndex(s => s.id === id);
}

export function getMaxAllowedIndex(): number {
  const saved = localStorage.getItem(STORAGE_KEY) as OnboardingStepId | null;
  if (!saved) return 0; // only /signup allowed if nothing completed
  const i = getStepIndexById(saved);
  return Math.min(i + 1, steps.length - 1); // allow next step after the last completed
}

export function markCompleted(stepId: OnboardingStepId) {
  localStorage.setItem(STORAGE_KEY, stepId);
}

export function resetOnboarding() {
  localStorage.removeItem(STORAGE_KEY);
}

export type OnboardingNav = {
  currentIndex: number;
  total: number;
  progress: number; // 0..1
  nextPath: string | null;
  prevPath: string | null;
  goNext: (navigate: (to: string) => void) => void;
  goPrev: (navigate: (to: string) => void) => void;
};

export function makeOnboardingNav(currentPath?: string): OnboardingNav {
  // Safe fallback if currentPath is undefined or empty
  const pathToUse = currentPath || "/";

  // Normalize: remove query params and trailing slash
  const cleanPath = pathToUse.split("?")[0].replace(/\/$/, "") || "/";

  const idx = steps.findIndex(s => s.path === cleanPath);

  const total = steps.length;
  const nextPath = idx < total - 1 && idx >= 0 ? steps[idx + 1].path : null;
  const prevPath = idx > 0 ? steps[idx - 1].path : null;

  const progress = idx >= 0 ? Math.max(0, idx / (total - 1)) : 0;

  return {
    currentIndex: idx,
    total,
    progress,
    nextPath,
    prevPath,
    goNext: (navigate) => { if (nextPath) navigate(nextPath); },
    goPrev: (navigate) => { if (prevPath) navigate(prevPath); },
  };
}
